import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  DollarSign, Wallet, Trophy, TrendingUp, Flame, Users, Play, Zap,
  Upload, Music, Video, Album, Radio, MessageSquare, Calendar, Megaphone,
  Sparkles, ChevronRight, Award, ArrowUpRight, Crown, Loader2, Check,
  Film, Mic2, Hash, MapPin, ShoppingBag, Palette, Handshake, GraduationCap,
  Bot, Bell, Store, Wrench, Camera, Gift, ArrowRight, PlusCircle,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════
function money(n) {
  const num = Number(n || 0);
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 10_000) return `$${(num / 1000).toFixed(1)}k`;
  return `$${num.toFixed(2)}`;
}

function formatInt(n) {
  const num = Number(n || 0);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1000).toFixed(1)}k`;
  return num.toLocaleString();
}

// Animated counter that eases into value on mount
function AnimatedNumber({ value, prefix = '', suffix = '', decimals = 0, className = '' }) {
  const [display, setDisplay] = useState(0);
  const raf = useRef();
  const start = useRef(null);
  const from = useRef(0);
  useEffect(() => {
    from.current = display;
    start.current = null;
    const duration = 800;
    const tick = (ts) => {
      if (start.current === null) start.current = ts;
      const p = Math.min(1, (ts - start.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(from.current + (value - from.current) * eased);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [value]);
  return (
    <span className={className}>
      {prefix}{Number(display).toFixed(decimals)}{suffix}
    </span>
  );
}

// ═══════════════════════════════════════════════════════
// Quick Actions
// ═══════════════════════════════════════════════════════
const QUICK_ACTIONS = [
  { key: 'upload_video', label: 'Upload Video', icon: Video, href: '/creator-editor?type=video', tint: 'from-rose-500 to-fuchsia-500' },
  { key: 'upload_music', label: 'Upload Music', icon: Music, href: '/creator-editor?type=track', tint: 'from-violet-500 to-indigo-500' },
  { key: 'release_single', label: 'Release Single', icon: Album, href: '/creator-editor?type=single', tint: 'from-emerald-500 to-teal-500' },
  { key: 'release_album', label: 'Release Album', icon: Sparkles, href: '/creator-editor?type=album', tint: 'from-amber-500 to-orange-500' },
  { key: 'go_live', label: 'Go Live', icon: Radio, href: '/live', tint: 'from-red-500 to-pink-500', hot: true },
  { key: 'create_post', label: 'Create Post', icon: MessageSquare, href: '/post/create', tint: 'from-cyan-500 to-blue-500' },
  { key: 'create_event', label: 'Create Event', icon: Calendar, href: '/events', tint: 'from-lime-500 to-emerald-500' },
  { key: 'promote', label: 'Promote', icon: Megaphone, href: '/promote', tint: 'from-orange-500 to-red-500' },
];

// Mission icon by key
const MISSION_ICONS = {
  music: Music, film: Film, radio: Radio, message: MessageSquare, users: Users,
  megaphone: Megaphone, video: Video, trending: TrendingUp,
};

// ═══════════════════════════════════════════════════════
// UI atoms
// ═══════════════════════════════════════════════════════

function GlassCard({ className = '', children, testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative overflow-hidden rounded-2xl bg-card/50 backdrop-blur-xl border border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.15)] ${className}`}
    >
      {children}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, hint, tint = 'from-violet-500 to-fuchsia-500', prefix = '', suffix = '', decimals = 0, testId }) {
  return (
    <GlassCard className="p-4 group hover:border-white/20 transition-all hover:-translate-y-0.5" testId={testId}>
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${tint} flex items-center justify-center shadow-lg`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        {hint && (
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 flex items-center gap-1">
            <ArrowUpRight className="w-3 h-3" />
            {hint}
          </div>
        )}
      </div>
      <div className="mt-3">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</p>
        <p className="text-2xl font-black tracking-tight" data-testid={`${testId}-value`}>
          <AnimatedNumber value={Number(value || 0)} prefix={prefix} suffix={suffix} decimals={decimals} />
        </p>
      </div>
    </GlassCard>
  );
}

function QuickActionButton({ action, onClick }) {
  const Icon = action.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`quick-action-${action.key}`}
      className="group flex flex-col items-center gap-2 p-3 rounded-2xl bg-card/40 backdrop-blur border border-white/10 hover:border-white/25 hover:-translate-y-0.5 transition-all min-w-[92px]"
    >
      <div className={`relative w-11 h-11 rounded-xl bg-gradient-to-br ${action.tint} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
        <Icon className="w-5 h-5 text-white" />
        {action.hot && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse ring-2 ring-background" />
        )}
      </div>
      <span className="text-xs font-semibold text-center">{action.label}</span>
    </button>
  );
}

function Rail({ title, icon: Icon, testId, right, children, empty }) {
  return (
    <section className="mb-10" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5 text-fuchsia-400" />}
          <h2 className="text-lg sm:text-xl font-black uppercase tracking-tight">{title}</h2>
        </div>
        {right}
      </div>
      {empty ? (
        <GlassCard className="p-8 text-center text-sm text-muted-foreground">
          Nothing here yet — check back soon.
        </GlassCard>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-3 -mx-4 px-4 snap-x snap-mandatory scroll-smooth">
          {children}
        </div>
      )}
    </section>
  );
}

function TrackTile({ t, onClick }) {
  const cover = t.cover_art_url || t.cover_url || t.thumbnail_url;
  return (
    <button
      onClick={onClick}
      className="group shrink-0 w-40 snap-start text-left"
      data-testid={`track-tile-${t.track_id}`}
    >
      <div className="relative aspect-square rounded-2xl overflow-hidden bg-muted/30 mb-2">
        {cover ? (
          <img src={cover} alt={t.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-violet-500/40 to-fuchsia-500/40 flex items-center justify-center">
            <Music className="w-8 h-8 text-white/40" />
          </div>
        )}
        <div className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all shadow-lg">
          <Play className="w-4 h-4 text-black fill-black ml-0.5" />
        </div>
      </div>
      <p className="font-semibold text-sm line-clamp-1">{t.title}</p>
      <p className="text-xs text-muted-foreground line-clamp-1">{t.artist_name}</p>
    </button>
  );
}

function VideoTile({ v, onClick }) {
  const cover = v.thumbnail_url || v.cover_url || v.poster_url;
  return (
    <button
      onClick={onClick}
      className="group shrink-0 w-56 snap-start text-left"
      data-testid={`video-tile-${v.reel_id || v.video_id}`}
    >
      <div className="relative aspect-video rounded-2xl overflow-hidden bg-muted/30 mb-2">
        {cover ? (
          <img src={cover} alt={v.title || v.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-rose-500/40 to-fuchsia-500/40 flex items-center justify-center">
            <Film className="w-8 h-8 text-white/40" />
          </div>
        )}
      </div>
      <p className="font-semibold text-sm line-clamp-1">{v.title || v.caption || 'Untitled'}</p>
      <p className="text-xs text-muted-foreground line-clamp-1">{v.author_name || v.artist_name}</p>
    </button>
  );
}

function CreatorTile({ c, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group shrink-0 w-36 snap-start text-center"
      data-testid={`creator-tile-${c.user_id}`}
    >
      <div className="relative w-32 h-32 mx-auto rounded-full overflow-hidden bg-muted/30 mb-2 border-2 border-border group-hover:border-fuchsia-500 transition-colors">
        {c.avatar ? (
          <img src={c.avatar} alt={c.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-2xl font-black text-white">
            {(c.name || '?')[0]}
          </div>
        )}
      </div>
      <p className="font-semibold text-sm line-clamp-1">{c.name || c.handle}</p>
      <p className="text-xs text-muted-foreground">{formatInt(c.followers)} followers</p>
    </button>
  );
}

// ═══════════════════════════════════════════════════════
// AI Daily Mission
// ═══════════════════════════════════════════════════════
function MissionCard({ mission, onComplete, onNavigate }) {
  const [saving, setSaving] = useState(false);
  if (!mission) return null;
  const Icon = MISSION_ICONS[mission.icon] || Sparkles;
  const done = mission.status === 'completed';
  const pct = done ? 100 : (mission.progress_pct || 0);

  return (
    <GlassCard className="p-6 relative overflow-hidden" testId="mission-card">
      {/* Decorative gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-violet-600/10 via-fuchsia-500/10 to-orange-500/10 pointer-events-none" />
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-fuchsia-500/10 blur-3xl pointer-events-none" />

      <div className="relative">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-orange-500 flex items-center justify-center shadow-lg">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-fuchsia-400 flex items-center gap-1">
                <Bot className="w-3 h-3" />
                AI Daily Mission
              </span>
              {done && (
                <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/40 text-[10px] uppercase font-black" data-testid="mission-done-badge">
                  <Check className="w-3 h-3 mr-0.5" /> Done
                </Badge>
              )}
            </div>
            <h3 className="text-xl font-black leading-tight" data-testid="mission-title">{mission.title}</h3>
            {mission.reason && (
              <p className="text-sm text-muted-foreground mt-1.5 leading-snug">{mission.reason}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 mt-4 text-xs">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400 font-bold">
            <Sparkles className="w-3 h-3" /> +{mission.xp} XP
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 font-bold">
            <DollarSign className="w-3 h-3" /> +${mission.est_uplift_usd?.toFixed(2)} est. today
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1 text-[11px] uppercase font-bold tracking-widest text-muted-foreground">
            <span>Progress</span><span>{pct}%</span>
          </div>
          <Progress value={pct} className="h-2" />
        </div>

        <div className="flex gap-2 mt-5">
          <Button
            onClick={() => onNavigate(mission.cta_href)}
            className="flex-1 bg-gradient-to-r from-fuchsia-500 to-orange-500 hover:from-fuchsia-600 hover:to-orange-600 text-white"
            data-testid="mission-cta"
          >
            {mission.cta} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button
            variant="outline"
            disabled={done || saving}
            onClick={async () => { setSaving(true); await onComplete(); setSaving(false); }}
            data-testid="mission-complete-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (done ? 'Completed' : 'Mark Done')}
          </Button>
        </div>
      </div>
    </GlassCard>
  );
}

// ═══════════════════════════════════════════════════════
// Money Center
// ═══════════════════════════════════════════════════════
const SOURCE_ICONS = {
  qualified_views: Play, tips: DollarSign, gifts: Gift, coins: Sparkles,
  subscriptions: Crown, ppv: ShoppingBag, merchandise: ShoppingBag, sponsorship: Handshake,
};

function MoneyCenter({ data, onCashout }) {
  if (!data) return <Skeleton className="h-96 rounded-2xl" />;
  const { windows, balances, sources, cashout } = data;

  return (
    <GlassCard className="p-6" testId="money-center">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
            <Wallet className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-black text-emerald-400">Money Center</p>
            <h2 className="text-xl font-black">Your Earnings</h2>
          </div>
        </div>
        <Button
          disabled={!cashout?.can_request}
          onClick={onCashout}
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg"
          data-testid="cashout-btn"
        >
          <DollarSign className="w-4 h-4 mr-1" /> Cash Out
        </Button>
      </div>

      {/* Window totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          { k: 'today_usd', l: 'Today' },
          { k: 'week_usd', l: 'This Week' },
          { k: 'month_usd', l: 'This Month' },
          { k: 'lifetime_usd', l: 'Lifetime' },
        ].map(w => (
          <div key={w.k} className="p-3 rounded-xl bg-background/40 border border-white/5" data-testid={`money-window-${w.k}`}>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{w.l}</p>
            <p className="text-lg font-black">
              <AnimatedNumber value={windows[w.k]} prefix="$" decimals={2} />
            </p>
          </div>
        ))}
      </div>

      {/* Balances */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/25">
          <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">Available</p>
          <p className="text-2xl font-black text-emerald-400">
            <AnimatedNumber value={balances.available_usd} prefix="$" decimals={2} />
          </p>
        </div>
        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/25">
          <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Pending</p>
          <p className="text-2xl font-black text-amber-400">
            <AnimatedNumber value={balances.pending_usd} prefix="$" decimals={2} />
          </p>
        </div>
      </div>

      {/* Sources */}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-2">Revenue by Source (30d)</p>
        <div className="grid grid-cols-2 gap-2">
          {sources.map(s => {
            const Icon = SOURCE_ICONS[s.key] || DollarSign;
            return (
              <div key={s.key} className="flex items-center justify-between p-2.5 rounded-lg bg-background/40 border border-white/5" data-testid={`money-source-${s.key}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className="w-4 h-4 text-fuchsia-400 shrink-0" />
                  <span className="text-xs font-semibold truncate">{s.label}</span>
                </div>
                <span className="text-xs font-black tabular-nums">{money(s.amount_30d_usd)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {!cashout?.can_request && (
        <p className="text-[11px] text-muted-foreground mt-4 text-center">
          Minimum cashout is {money(cashout?.min_payout_usd || 25)}.
          You need <span className="font-bold text-emerald-400">{money((cashout?.min_payout_usd || 25) - (balances.available_usd || 0))}</span> more available to cash out.
        </p>
      )}
    </GlassCard>
  );
}

// ═══════════════════════════════════════════════════════
// Cashout Modal
// ═══════════════════════════════════════════════════════
function CashoutModal({ open, onOpenChange, available, minPayout, onSubmit, connectStatus, onSetupPayouts }) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('stripe');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) setAmount(String(available.toFixed(2))); }, [open, available]);

  const needsConnect = method === 'stripe' && connectStatus !== 'enabled';

  const submit = async () => {
    setSubmitting(true);
    const ok = await onSubmit({
      amount_usd: parseFloat(amount),
      method,
      payout_email: email || undefined,
      notes: notes || undefined,
    });
    setSubmitting(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cashout-modal">
        <DialogHeader>
          <DialogTitle>Request Cashout</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-between">
            <span className="text-sm font-semibold">Available</span>
            <span className="text-lg font-black text-emerald-400">{money(available)}</span>
          </div>
          <div>
            <Label className="text-xs">Amount (min {money(minPayout)})</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              step="0.01"
              min={minPayout}
              max={available}
              data-testid="cashout-amount"
            />
          </div>
          <div>
            <Label className="text-xs">Payout method</Label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {[
                { k: 'stripe', l: 'Stripe' },
                { k: 'paypal', l: 'PayPal' },
                { k: 'zelle', l: 'Zelle' },
                { k: 'bank', l: 'Bank' },
              ].map(m => (
                <button
                  key={m.k}
                  onClick={() => setMethod(m.k)}
                  data-testid={`cashout-method-${m.k}`}
                  className={`py-2 rounded-lg border text-xs font-semibold ${
                    method === m.k
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'border-border hover:border-emerald-500/50'
                  }`}
                >
                  {m.l}
                </button>
              ))}
            </div>
          </div>
          {needsConnect && (
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30" data-testid="connect-required-notice">
              <p className="text-xs font-semibold text-amber-400 mb-1">Stripe payout account required</p>
              <p className="text-[11px] text-muted-foreground mb-2">
                To receive Stripe payouts directly to your bank, first set up your Stripe Connect account (takes ~3 min).
              </p>
              <Button
                size="sm"
                onClick={onSetupPayouts}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="cashout-setup-payouts"
              >
                Set Up Payouts <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}
          {method !== 'stripe' && (
            <div>
              <Label className="text-xs">Payout email/handle</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="cashout-email" />
            </div>
          )}
          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the admin should know" data-testid="cashout-notes" />
          </div>
          <p className="text-[11px] text-muted-foreground">
            {method === 'stripe'
              ? 'Approved payouts are sent to your Stripe bank account. Arrival: 2-3 business days.'
              : 'Manual review request. Admin will process within 1-3 business days.'}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || !amount || parseFloat(amount) < minPayout || needsConnect}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
            data-testid="cashout-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════
// Premium marketplace tiles (Phase 3 shortcuts)
// ═══════════════════════════════════════════════════════
const PREMIUM_TILES = [
  { key: 'charts', label: 'Top Charts', desc: "See who's dominating this week", icon: Trophy, tint: 'from-amber-500 to-yellow-500', href: '/charts' },
  { key: 'marketplace', label: 'Creator Marketplace', desc: 'Hire producers, videographers, designers', icon: Store, tint: 'from-violet-500 to-fuchsia-500', href: '/marketplace' },
  { key: 'promo', label: 'Paid Promotion', desc: 'Boost your best track to the top', icon: Megaphone, tint: 'from-orange-500 to-red-500', href: '/promote' },
  { key: 'beats', label: 'Beat Marketplace', desc: 'License beats from top producers', icon: Music, tint: 'from-blue-500 to-cyan-500', href: '/beat-store' },
  { key: 'studios', label: 'Studio Booking', desc: 'Book DMV recording studios', icon: Mic2, tint: 'from-fuchsia-500 to-pink-500', href: '/studios' },
  { key: 'merch', label: 'Merchandise', desc: 'Sell shirts, vinyl, custom merch', icon: ShoppingBag, tint: 'from-emerald-500 to-teal-500', href: '/store' },
  { key: 'sponsors', label: 'Sponsorship Ops', desc: 'Brand deals matched to your audience', icon: Handshake, tint: 'from-indigo-500 to-purple-500', href: '/hire-board' },
  { key: 'events', label: 'Upcoming Events', desc: 'Shows, drops, industry meetups', icon: Calendar, tint: 'from-lime-500 to-emerald-500', href: '/events' },
  { key: 'academy', label: 'Creator Academy', desc: 'Free lessons to level up', icon: GraduationCap, tint: 'from-yellow-500 to-orange-500', href: '/academy' },
  { key: 'ai_coach', label: 'AI Creator Coach', desc: 'Personalized guidance from Claude', icon: Bot, tint: 'from-purple-500 to-pink-500', href: '/coach' },
  { key: 'referrals', label: 'Refer & Earn', desc: 'Invite creators — you both get $5', icon: Gift, tint: 'from-rose-500 to-fuchsia-500', href: '/referrals' },
];

function PremiumTile({ tile, onClick }) {
  const Icon = tile.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`premium-tile-${tile.key}`}
      className="group relative overflow-hidden rounded-2xl bg-card/60 backdrop-blur border border-white/10 hover:border-white/25 p-4 text-left transition-all hover:-translate-y-0.5"
    >
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${tile.tint} flex items-center justify-center shadow-lg mb-3`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h3 className="font-black text-base">{tile.label}</h3>
      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{tile.desc}</p>
      <ChevronRight className="w-4 h-4 text-muted-foreground absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════
export default function CreatorDashboardPage() {
  const { isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [hero, setHero] = useState(null);
  const [mission, setMission] = useState(null);
  const [money, setMoney] = useState(null);
  const [discovery, setDiscovery] = useState(null);
  const [connectStatus, setConnectStatus] = useState('not_started');
  const [loading, setLoading] = useState(true);
  const [cashoutOpen, setCashoutOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    try {
      const [h, m, mc, disc, cs] = await Promise.all([
        fetch(`${API}/dashboard/hero`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/dashboard/mission/today`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/dashboard/money-center`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/dashboard/discovery`, { headers }).then(r => r.ok ? r.json() : null),
        fetch(`${API}/payouts/connect/status`, { headers }).then(r => r.ok ? r.json() : null),
      ]);
      setHero(h);
      setMission(m);
      setMoney(mc);
      setDiscovery(disc);
      if (cs) setConnectStatus(cs.status || 'not_started');
    } catch { /* noop */ }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/landing'); return; }
    fetchAll();
  }, [authLoading, isAuthenticated, fetchAll, navigate]);

  const handleMissionComplete = async () => {
    try {
      const res = await fetch(`${API}/dashboard/mission/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ progress_pct: 100 }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(`Mission complete! +${data.xp_awarded} XP`);
      setMission(prev => ({ ...prev, status: 'completed', progress_pct: 100 }));
    } catch {
      toast.error('Could not update mission');
    }
  };

  const handleCashout = async (payload) => {
    try {
      const res = await fetch(`${API}/dashboard/cashout-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Cashout request failed');
        return false;
      }
      toast.success('Cashout request submitted — admin will review shortly.');
      fetchAll();
      return true;
    } catch {
      toast.error('Cashout request failed');
      return false;
    }
  };

  if (loading || !hero) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-32 mb-6 rounded-2xl" />
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}
          </div>
          <Skeleton className="h-48 mb-6 rounded-2xl" />
          <Skeleton className="h-80 rounded-2xl" />
        </div>
      </div>
    );
  }

  const g = hero.greeting;
  const e = hero.earnings;
  const w = hero.wallet;
  const s = hero.score;
  const gr = hero.growth;

  return (
    <div className="min-h-screen bg-background">
      {/* ═══ Ambient bg gradient ═══ */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-fuchsia-600/10 blur-[120px]" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-violet-600/10 blur-[120px]" />
      </div>

      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* ═══ Greeting ═══ */}
        <header className="mb-6 flex items-center gap-4" data-testid="dashboard-greeting">
          {g.avatar ? (
            <img src={g.avatar} alt={g.name} className="w-14 h-14 rounded-2xl object-cover border-2 border-white/10" />
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-2xl font-black text-white">
              {g.name?.[0]}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{g.text}</p>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-tight">
              {g.name} <span className="text-fuchsia-400">👋</span>
            </h1>
          </div>
          {g.is_studio_plus && (
            <Badge className="bg-gradient-to-r from-amber-500 to-fuchsia-500 text-white border-none font-black" data-testid="studio-plus-chip">
              <Crown className="w-3 h-3 mr-1" /> Studio+
            </Badge>
          )}
        </header>

        {/* ═══ Hero stat tiles ═══ */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6" data-testid="hero-stats">
          <StatTile
            icon={DollarSign} label="Today's Earnings" value={e.today_usd}
            prefix="$" decimals={2} tint="from-emerald-500 to-teal-500"
            hint={e.delta_vs_yesterday > 0 ? `+${money(e.delta_vs_yesterday)}` : null}
            testId="stat-today-earnings"
          />
          <StatTile
            icon={Wallet} label="Available" value={w.available_usd}
            prefix="$" decimals={2} tint="from-cyan-500 to-blue-500"
            testId="stat-available"
          />
          <StatTile
            icon={ArrowUpRight} label="Pending" value={w.pending_usd}
            prefix="$" decimals={2} tint="from-amber-500 to-orange-500"
            testId="stat-pending"
          />
          <StatTile
            icon={TrendingUp} label="Est. Today" value={e.estimated_today_usd}
            prefix="$" decimals={2} tint="from-violet-500 to-fuchsia-500"
            testId="stat-est-today"
          />
          <StatTile
            icon={Trophy} label={s.rank ? `Rank #${s.rank}` : 'Creator Score'} value={s.lifetime}
            tint="from-amber-500 to-yellow-500"
            testId="stat-score"
          />
          <StatTile
            icon={Flame} label="Upload Streak" value={gr.upload_streak_days} suffix=" days"
            tint="from-red-500 to-pink-500"
            testId="stat-streak"
          />
          <StatTile
            icon={Users} label="Followers Today" value={gr.followers_gained_today}
            tint="from-fuchsia-500 to-pink-500"
            testId="stat-followers-today"
          />
          <StatTile
            icon={Play} label="Qualified Streams Today" value={gr.qualified_streams_today}
            tint="from-indigo-500 to-purple-500"
            testId="stat-qualified-today"
          />
        </div>

        {/* ═══ Quick actions row ═══ */}
        <div className="mb-6" data-testid="quick-actions">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-black uppercase tracking-widest">Quick Actions</h2>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
            {QUICK_ACTIONS.map(a => (
              <QuickActionButton
                key={a.key}
                action={a}
                onClick={() => navigate(a.href)}
              />
            ))}
          </div>
        </div>

        {/* ═══ Mission + Money row ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <MissionCard
            mission={mission}
            onComplete={handleMissionComplete}
            onNavigate={(href) => navigate(href)}
          />
          <MoneyCenter data={money} onCashout={() => setCashoutOpen(true)} />
        </div>

        {/* ═══ Discovery rails ═══ */}
        {discovery && (
          <>
            <Rail title="Trending Videos" icon={Video} testId="rail-trending-videos" empty={!discovery.trending_videos?.length}>
              {(discovery.trending_videos || []).map(v => (
                <VideoTile key={v.reel_id || v.video_id} v={v} onClick={() => navigate(`/reel/${v.reel_id}`)} />
              ))}
            </Rail>
            <Rail title="Trending Songs" icon={Music} testId="rail-trending-songs" empty={!discovery.trending_songs?.length}>
              {(discovery.trending_songs || []).map(t => (
                <TrackTile key={t.track_id} t={t} onClick={() => navigate(`/track/${t.track_id}`)} />
              ))}
            </Rail>
            <Rail title="Trending Albums" icon={Album} testId="rail-trending-albums" empty={!discovery.trending_albums?.length}>
              {(discovery.trending_albums || []).map(t => (
                <TrackTile key={t.track_id} t={t} onClick={() => navigate(`/track/${t.track_id}`)} />
              ))}
            </Rail>
            <Rail title="Trending Creators" icon={Users} testId="rail-trending-creators" empty={!discovery.trending_creators?.length}>
              {(discovery.trending_creators || []).map(c => (
                <CreatorTile key={c.user_id} c={c} onClick={() => navigate(c.handle ? `/@${c.handle}` : `/artist/${c.user_id}`)} />
              ))}
            </Rail>
            <Rail title="New Singles" icon={Sparkles} testId="rail-new-singles" empty={!discovery.new_singles?.length}>
              {(discovery.new_singles || []).map(t => (
                <TrackTile key={t.track_id} t={t} onClick={() => navigate(`/track/${t.track_id}`)} />
              ))}
            </Rail>
            <Rail title="New Albums" icon={Album} testId="rail-new-albums" empty={!discovery.new_albums?.length}>
              {(discovery.new_albums || []).map(t => (
                <TrackTile key={t.track_id} t={t} onClick={() => navigate(`/track/${t.track_id}`)} />
              ))}
            </Rail>
            <Rail title="New Music Videos" icon={Film} testId="rail-new-music-videos" empty={!discovery.new_music_videos?.length}>
              {(discovery.new_music_videos || []).map(v => (
                <VideoTile key={v.reel_id || v.video_id} v={v} onClick={() => navigate(`/reel/${v.reel_id}`)} />
              ))}
            </Rail>
            <Rail title="Local Trending (DMV)" icon={MapPin} testId="rail-local"
                  right={<Button variant="link" size="sm" onClick={() => navigate('/pulse')}>See Pulse <ArrowRight className="w-3 h-3 ml-1" /></Button>}
                  empty={!discovery.local_tracks?.length}>
              {(discovery.local_tracks || []).map(t => (
                <TrackTile key={t.track_id} t={t} onClick={() => navigate(`/track/${t.track_id}`)} />
              ))}
            </Rail>
            <Rail title="Live Creators" icon={Radio} testId="rail-live"
                  right={<Button variant="link" size="sm" onClick={() => navigate('/live')}>All Live <ArrowRight className="w-3 h-3 ml-1" /></Button>}
                  empty={!discovery.live_creators?.length}>
              {(discovery.live_creators || []).map(l => (
                <VideoTile key={l.stream_id || l.id} v={{ ...l, title: l.title || 'Live stream', thumbnail_url: l.thumbnail_url || l.cover_url }} onClick={() => navigate(`/watch/${l.stream_id || l.id}`)} />
              ))}
            </Rail>
            <Rail title="Upcoming Events" icon={Calendar} testId="rail-events"
                  right={<Button variant="link" size="sm" onClick={() => navigate('/events')}>All Events <ArrowRight className="w-3 h-3 ml-1" /></Button>}
                  empty={!discovery.upcoming_events?.length}>
              {(discovery.upcoming_events || []).map(ev => (
                <VideoTile key={ev.event_id} v={{ title: ev.title || ev.name, author_name: ev.venue_name, thumbnail_url: ev.cover_url || ev.flyer_url }} onClick={() => navigate(`/events/${ev.event_id}`)} />
              ))}
            </Rail>
            <Rail title="Recommended For You" icon={Sparkles} testId="rail-recommended" empty={!discovery.recommended_tracks?.length}>
              {(discovery.recommended_tracks || []).map(t => (
                <TrackTile key={t.track_id} t={t} onClick={() => navigate(`/track/${t.track_id}`)} />
              ))}
            </Rail>

            {/* ═══ Trending hashtags ═══ */}
            {discovery.trending_hashtags?.length > 0 && (
              <section className="mb-10" data-testid="rail-hashtags">
                <div className="flex items-center gap-2 mb-3">
                  <Hash className="w-5 h-5 text-fuchsia-400" />
                  <h2 className="text-lg sm:text-xl font-black uppercase tracking-tight">Trending Hashtags</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {discovery.trending_hashtags.map(h => (
                    <button
                      key={h.tag}
                      onClick={() => navigate(`/search?q=%23${h.tag}`)}
                      data-testid={`hashtag-${h.tag}`}
                      className="px-3 py-1.5 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 text-sm text-fuchsia-300 hover:bg-fuchsia-500/20 hover:border-fuchsia-500/60 transition-colors"
                    >
                      #{h.tag}
                      <span className="ml-1.5 text-[10px] text-muted-foreground">{h.count}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* ═══ Premium tiles (Phase 3) ═══ */}
        <section className="mb-10" data-testid="premium-tiles">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg sm:text-xl font-black uppercase tracking-tight">Explore Opportunities</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {PREMIUM_TILES.map(t => (
              <PremiumTile key={t.key} tile={t} onClick={() => navigate(t.href)} />
            ))}
          </div>
        </section>

        {/* Foot */}
        <p className="text-center text-[11px] text-muted-foreground mt-12 mb-4">
          Every action counts. Keep uploading, keep engaging, keep earning.
        </p>
      </div>

      <CashoutModal
        open={cashoutOpen}
        onOpenChange={setCashoutOpen}
        available={money?.balances?.available_usd || 0}
        minPayout={money?.cashout?.min_payout_usd || 25}
        onSubmit={handleCashout}
        connectStatus={connectStatus}
        onSetupPayouts={() => { setCashoutOpen(false); navigate('/settings/payouts'); }}
      />
    </div>
  );
}
